import {
    Box,
    Button,
    Card,
    Checkbox,
    Form,
    FormButtonRow,
    FormGroup,
    FormParagraph,
    FormSection,
    IconButton,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import type { ValidationErrors } from '../../../utils/validation';
import type { IServiceConsumer } from '../../interfaces/serviceConsumer';
import { ServiceType } from '../../../../common/enums/serviceType';
import { employmentZorgmailNummerSchema } from '../../../../common/validation/employment';
import { serviceConsumerZorgMailPreferencesSchema } from '../../../../common/validation/serviceConsumer';
import { translateKey } from '../../../translate';
import { getValidationErrorTranslation } from '../../../utils/getValidationErrorTranslation';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { emptyValidationErrors, validate } from '../../../utils/validation';

interface IZorgMailSettingsProps {
    employmentId: string;
    hasActiveServiceConsumer: boolean;
    isReadOnly: boolean;
}

interface IEmployment {
    id: string;
    zorgmailNummer: string;
}

interface IZorgMailTypes {
    sendZorgMailOnExaminationRequest: boolean;
    sendZorgMailOnReferralIntake: boolean;
    sendZorgMailOnReferralRequest: boolean;
}

interface IServiceConsumerWithZorgMailSettings extends IServiceConsumer, IZorgMailTypes {
    hasChanged?: boolean;
}

export function ZorgMailSettings(props: IZorgMailSettingsProps) {
    const abortableFetch = useAbortableFetch();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [originalEmployment, setOriginalEmployment] = useState<IEmployment>();
    const [employment, setEmployment] = useState<IEmployment>();

    const [originalServiceConsumers, setOriginalServiceConsumers] = useState<IServiceConsumerWithZorgMailSettings[]>(
        []
    );
    const [serviceConsumers, setServiceConsumers] = useState<IServiceConsumerWithZorgMailSettings[]>([]);

    useEffect(() => {
        loadEmployment(props.employmentId);
        loadServiceConsumers(props.employmentId);
    }, [props.employmentId]);

    return (
        <>
            {employment && serviceConsumers && (
                <Card>
                    <Card.Transition type={isEditing ? 'edit' : 'read'} />
                    <Card.Header
                        action={
                            !props.isReadOnly && !isEditing ? (
                                <IconButton
                                    icon="edit-light"
                                    labelText="edit"
                                    onClick={() => setIsEditing(true)}
                                />
                            ) : undefined
                        }
                        title={translateKey('personalSettings.zorgMail')}
                    />
                    <Card.Body>
                        <Form
                            onSubmit={async (e) => {
                                e.preventDefault();
                                await handleSubmit();
                            }}
                            submitOnEnter={false}
                        >
                            <FormSection title={translateKey('personalSettings.yourZorgMail')}>
                                <Text mb={6}>{translateKey('personalSettings.zorgMailNumberExplanation')}</Text>
                                {renderTextInput()}
                            </FormSection>

                            {renderExtraZorgMailSettings()}

                            {isEditing && (
                                <FormButtonRow>
                                    <Button
                                        isDisabled={isSaving}
                                        onClick={() => handleCancel()}
                                        type="button"
                                        variant="outline"
                                    >
                                        {translateKey('common.cancel')}
                                    </Button>
                                    <Button
                                        isDisabled={isSaving}
                                        type="submit"
                                        variant="solid"
                                    >
                                        {translateKey('common.save')}
                                    </Button>
                                </FormButtonRow>
                            )}
                        </Form>
                    </Card.Body>
                </Card>
            )}
        </>
    );

    function renderTextInput() {
        if (isEditing) {
            return (
                <FormGroup label={translateKey('personalSettings.zorgMail')}>
                    <TextInput
                        error={getValidationErrorTranslation(errors.get('zorgmailNummer'))}
                        maxLength={9}
                        onBlur={() => {
                            return;
                        }}
                        onChange={(e) => handleEmploymentChange('zorgmailNummer', e.target.value)}
                        placeholder={translateKey('personalSettings.zorgMailPlaceholder')}
                        value={employment?.zorgmailNummer ? employment?.zorgmailNummer : isEditing ? '' : '-'}
                    />
                </FormGroup>
            );
        }

        return (
            <FormParagraphFormGroup label={translateKey('personalSettings.zorgMail')}>
                {employment?.zorgmailNummer || '-'}
            </FormParagraphFormGroup>
        );
    }

    function renderExtraZorgMailSettings() {
        if (!props.hasActiveServiceConsumer || !employment?.zorgmailNummer) {
            return null;
        }

        const examinationConsumers = filterConsumersForServiceType(ServiceType.examination, true);
        const referralConsumers = filterConsumersForServiceType(ServiceType.referral);

        if (examinationConsumers.length === 0 && referralConsumers.length === 0) {
            return null;
        }

        return (
            <FormSection title={translateKey('personalSettings.extraEdifacts')}>
                {renderExtraZorgMailSetting(examinationConsumers, 'sendZorgMailOnExaminationRequest')}
                {renderExtraZorgMailSetting(referralConsumers, 'sendZorgMailOnReferralRequest')}
                {renderExtraZorgMailSetting(referralConsumers, 'sendZorgMailOnReferralIntake')}
            </FormSection>
        );
    }

    function filterConsumersForServiceType(serviceType: ServiceType, isRequestCreatorOnly: boolean = false) {
        return serviceConsumers.filter(
            (serviceConsumer) =>
                serviceConsumer.service.serviceType === serviceType &&
                (!isRequestCreatorOnly || serviceConsumer.isRequestCreator)
        );
    }

    function renderExtraZorgMailSetting(
        consumers: IServiceConsumerWithZorgMailSettings[],
        mailType: keyof IZorgMailTypes
    ) {
        return (
            consumers.length > 0 && (
                <FormGroup label={translateKey(`personalSettings.${mailType}`)}>
                    {!isEditing && (
                        <FormParagraph>
                            {consumers
                                .filter((consumer) => consumer[mailType])
                                .map((consumer) => consumer.service.name)
                                .join(', ') || '-'}
                        </FormParagraph>
                    )}
                    {isEditing && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={2}
                            mt={[0, null, 1]}
                        >
                            {consumers
                                .filter((consumer) => consumer.authorization && consumer.authorization.canUpdate)
                                .map((consumer) => (
                                    <Checkbox
                                        checked={consumer[mailType]}
                                        id={`${mailType}_${consumer.id}`}
                                        key={consumer.id}
                                        label={consumer.service.name}
                                        onChange={() =>
                                            handleServiceConsumerChange(consumer.id, mailType, !consumer[mailType])
                                        }
                                    />
                                ))}
                        </Box>
                    )}
                </FormGroup>
            )
        );
    }

    async function loadEmployment(employmentId: string) {
        const url = `/api/employments/${employmentId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setOriginalEmployment(result.json);
        setEmployment(result.json);
    }

    async function loadServiceConsumers(employmentId: string) {
        const result = await abortableFetch.fetchJson(
            `/api/serviceConsumers?employmentId=${employmentId}${getIsActiveQueryParameter()}&includeItemAuthorizations=canUpdate`,
            'GET'
        );

        setOriginalServiceConsumers(result.json.items);
        setServiceConsumers(result.json.items);
    }

    function handleEmploymentChange<K extends keyof IEmployment>(name: K, value: IEmployment[K]) {
        setEmployment({ ...employment, [name]: value || null } as IEmployment);
    }

    function handleServiceConsumerChange<K extends keyof IServiceConsumerWithZorgMailSettings>(
        serviceConsumerId: string,
        name: K,
        value: IServiceConsumerWithZorgMailSettings[K]
    ) {
        const updatedServiceConsumers = serviceConsumers.map((serviceConsumer) => {
            if (serviceConsumer.id === serviceConsumerId) {
                return { ...serviceConsumer, [name]: value, hasChanged: true };
            }

            return serviceConsumer;
        });
        setServiceConsumers(updatedServiceConsumers);
    }

    function handleCancel() {
        setEmployment(originalEmployment);
        setServiceConsumers(originalServiceConsumers);
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    function getIsActiveQueryParameter() {
        return props.isReadOnly ? '' : '&isActive=true';
    }

    async function handleSubmit() {
        setIsSaving(true);

        if (employment && originalEmployment && employment.zorgmailNummer !== originalEmployment.zorgmailNummer) {
            const { errors: validationErrors, item } = validate(employmentZorgmailNummerSchema, employment);

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
                setIsSaving(false);

                return;
            } else {
                const url = `/api/employments/${employment.id}`;
                await abortableFetch.fetchJson(url, 'POST', item);
                setOriginalEmployment(employment);
            }
        }

        const changedServiceConsumers = serviceConsumers.filter((serviceConsumer) => serviceConsumer.hasChanged);
        for (const serviceConsumer of changedServiceConsumers) {
            const { item } = validate(serviceConsumerZorgMailPreferencesSchema, serviceConsumer);
            await abortableFetch.fetchJson(
                `/api/serviceConsumers/${serviceConsumer.id}/zorgMailPreferences`,
                'POST',
                item
            );
        }

        const savedServiceConsumers = serviceConsumers.map((serviceConsumer) => ({
            ...serviceConsumer,
            hasChanged: false,
        }));
        setOriginalServiceConsumers(savedServiceConsumers);
        setServiceConsumers(savedServiceConsumers);

        setErrors(emptyValidationErrors);
        setIsEditing(false);
        setIsSaving(false);
    }
}
